import { StatusRow } from '../models/git-worker.model';
import { treeToArray } from '../util/tree-to-array';
import { WorkerAction, WorkerActionError, WorkerFnParams } from '../wasm-git.worker.model';
import type { GitAction } from './actions.enum';

/**
 * https://isomorphic-git.org/docs/en/statusMatrix.html
 *
 ```js
 * // example StatusMatrix
 * [
 *   ["a.txt", 0, 2, 0], // new, untracked
 *   ["b.txt", 0, 2, 2], // added, staged
 *   ["c.txt", 0, 2, 3], // added, staged, with unstaged changes
 *   ["d.txt", 1, 1, 1], // unmodified
 *   ["e.txt", 1, 2, 1], // modified, unstaged
 *   ["f.txt", 1, 2, 2], // modified, staged
 *   ["g.txt", 1, 2, 3], // modified, staged, with unstaged changes
 *   ["h.txt", 1, 0, 1], // deleted, unstaged
 *   ["i.txt", 1, 0, 0], // deleted, staged
 *   Conflicts
 *   ["k.txt", 2, 3, 4], // UU, unmerged(both modified)
 *   ["l.txt", 0, 3, 4], // AA, unmerged(both added)
 *   ["m.txt", 2, 0, 4], // DU, unmerged(deleted by us)
 *   ["n.txt", 2, 3, 0], // UD, unmerged(deleted by them)
 *   ["o.txt", 2, 0, 0], // DD, both deleted
 *   ["p.txt", 0, 3, 0], // AU, unmerged(added by us)
 * ]
 * ```
 *
 * | HEAD | WORKDIR | STAGE | `git status --short` equivalent |
 * | ---- | ------- | ----- | ------------------------------- |
 * | 0    | 0       | 0     | ``                              |
 * | 0    | 0       | 3     | `AD`                            |
 * | 0    | 2       | 0     | `??`                            |
 * | 0    | 2       | 2     | `A `                            |
 * | 0    | 2       | 3     | `AM`                            |
 * | 1    | 0       | 0     | `D `                            |
 * | 1    | 0       | 1     | ` D`                            |
 * | 1    | 0       | 3     | `MD`                            |
 * | 1    | 1       | 0     | `D ` + `??`                     |
 * | 1    | 1       | 1     | ``                              |
 * | 1    | 1       | 3     | `MM`                            |
 * | 1    | 2       | 0     | `D ` + `??`                     |
 * | 1    | 2       | 1     | ` M`                            |
 * | 1    | 2       | 2     | `M `                            |
 * | 1    | 2       | 3     | `MM`                            |
 * Conflicts
 * | 2    | 3       | 4     | `UU`                            |
 * | 0    | 3       | 4     | `AA`                            |
 * | 2    | 0       | 4     | `DU`                            |
 * | 2    | 3       | 0     | `UD`                            |
 * | 2    | 0       | 0     | `DD`                            |
 * | 0    | 3       | 0     | `AU`                            |
 * | 0    | 0       | 4     | `UA`                            |
 */
export interface GitStatusShortAction extends WorkerAction {
  type: GitAction.StatusShort;
}

type Filename = string;
export type GitFileStatusRow = [Filename, ...StatusRow];

export type GitShortStatus =
  | '  '
  | 'AD'
  | '??'
  | 'A '
  | 'AM'
  | 'D '
  | ' D'
  | 'MD'
  | ' M'
  | 'M '
  | 'MM'
  // conflicts
  | 'UU' // BOTH_MODIFIED
  | 'AA' // BOTH_ADDED
  | 'DU' // OURS_DELETED
  | 'UD' // THEIRS_DELETED
  | 'DD' // BOTH_DELETED
  | 'AU' // ADDED_BY_US
  | 'UA'; // ADDED_BY_THEM
/**
 * - The HEAD status is either absent (0) or present (1), or conflicted(2).
 * - The WORKDIR status is either absent (0), identical to HEAD (1), or different from HEAD (2), or conflicted(3).
 * - The STAGE status is either absent (0), identical to HEAD (1), identical to WORKDIR (2), or different from WORKDIR (3). or conflicted(4).
 */
export const GitStatusToStatusRow: Record<GitShortStatus, StatusRow> = {
  '  ': [1, 1, 1], // unmodified
  AD: [0, 0, 3],
  '??': [0, 2, 0], // new, untracked
  'A ': [0, 2, 2], // added, staged
  AM: [0, 2, 3], // added, staged, with unstaged changes
  'D ': [1, 0, 0], // deleted, staged
  ' D': [1, 0, 1], // deleted, unstaged
  MD: [1, 0, 3],
  // 'D ??': [1, 1, 0],
  // MM: [1, 1, 3],
  // 'D ??': [1, 2, 0],
  ' M': [1, 2, 1], // modified, unstaged
  'M ': [1, 2, 2], // modified, staged
  MM: [1, 2, 3], // modified, staged, with unstaged changes
  // CONFLICTS
  UU: [2, 3, 4], // BOTH_MODIFIED
  AA: [0, 3, 4], // BOTH_ADDED
  DU: [2, 0, 4], // OURS_DELETED
  UD: [2, 3, 0], // THEIRS_DELETED
  DD: [2, 0, 0], // BOTH_DELETED
  AU: [0, 3, 0], // ADDED_BY_US
  UA: [0, 0, 4], // ADDED_BY_THEM
};

export class GitStatusShortActionError extends WorkerActionError {}

export async function GitStatusShortActionFn(
  params: WorkerFnParams<GitStatusShortAction>,
): Promise<GitFileStatusRow[]> {
  const { lg, util } = params;
  try {
    const stdout = lg.callAndCaptureOutput(['status', '--short']);
    const statusRows = stdout
      .split('\n')
      .map((row) => [row.slice(0, 2), row.slice(3)])
      .map(([status, name]) => {
        const statusRow: StatusRow | undefined = GitStatusToStatusRow[status as GitShortStatus];

        if (!statusRow) {
          return null;
        }

        return [name, ...statusRow] as GitFileStatusRow;
      })
      .filter(Boolean) as GitFileStatusRow[];

    const CONFLICT_MARKER = 'conflict: ';
    const conflictsStartIndex = stdout.indexOf(CONFLICT_MARKER);
    if (conflictsStartIndex > -1) {
      const toStatusNum = (value: string, exist: number) => (value === 'NULL' ? 0 : exist);
      // conflict: a:ancestor-path o:ours-path t:theirs-path
      const conflictsOutput = stdout.slice(conflictsStartIndex);
      const conflictedRows = conflictsOutput
        .split('\n')
        .map((row) => {
          const filePathsStr = row.slice(CONFLICT_MARKER.length);
          // a:ancestor-path o:ours-path t:theirs-path
          const matches = filePathsStr.match(/a:(.+) o:(.+) t:(.+)/);
          if (!matches) {
            return null;
          }
          const [, ancestor, ours, theirs] = matches;
          const statusRow = [toStatusNum(ancestor ?? '', 2), toStatusNum(ours ?? '', 3), toStatusNum(theirs ?? '', 4)];
          const name = [ancestor, ours, theirs].find((value) => value !== 'NULL');
          return [name, ...statusRow] as GitFileStatusRow;
        })
        .filter(Boolean) as GitFileStatusRow[];
      statusRows.push(...conflictedRows);
    }

    // if `git status --short` reports a single folder,
    // then it must contain new & untracked files
    // NB: if the new folder contains sub-folder, those will not be reported
    // so we do need to do a recursive search of all sub-folders as well
    const newFiles = statusRows
      .filter(([path]) => util.isDir(path))
      .map(([path]) => util.readFileTree(path))
      .map((dir) => treeToArray(dir))
      .map((treeItems) => treeItems.map(({ path }) => path))
      .flat()
      .map((path) => [path, ...GitStatusToStatusRow['??']] as GitFileStatusRow);

    // only report back actual files (not folders)
    return [...statusRows, ...newFiles].filter(([path]) => util.isFile(path));
  } catch (err) {
    console.error(err);
    return [];
  }
}
